import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  LinearProgress,
  useTheme,
} from '@mui/material';
import {
  SmartToy,
  LeaderboardRounded,
  EmailRounded,
  StackedBarChartRounded,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { getCampaignAnalytics, getDashboard, getEmailAnalytics } from '../api/apiService';
import { BarChart } from '@mui/x-charts';
import { PieChart } from '@mui/x-charts';

export default function Dashboard() {
  const theme = useTheme();
  const clientId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('clientId')) ?? '';
    } catch {
      return '';
    }
  }, []);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  //leads
  const [summary, setSummary] = useState({});
  const [pipeline, setPipeline] = useState([]);
  //campaigns
  const [campaignsSummary, setCampaignsSummary] = useState([]);
  //const [campaigns, setCampaigns] = useState([]);
//  const [selectedCampaign, setSelectedCampaign] = useState("all");

  const [activities, setActivities] = useState([]);
  //const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});

  // const metrics = [
  //   { label: "Emails Sent", value: stats.sent ?? 0},
  //   { label: "Emails Delivered", value: stats.delivered ?? 0},
  //   { label: "Emails Opened", value: stats.opened ?? 0},
  //   { label: "Emails Clicked", value: stats.clicked ?? 0},
  //   { label: "Emails Bounced", value: stats.bounced ?? 0},
  //   { label: "Emails Unsubscribed", value: stats.unsubscribed ?? 0},
  // ];

  // Filter campaigns for the graph based on selected value
  // const filteredCampaigns = campaigns

  // const xLabels = filteredCampaigns.map((c) => c.name);
  // const openData = filteredCampaigns.map((c) => c.openRate);
  // const clickData = filteredCampaigns.map((c) => c.clickRate);
  // const bounceData = filteredCampaigns.map((c) => c.bounceRate);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await getEmailAnalytics(clientId);
        setStats(res.data);
      } catch (err) {
        console.error("Failed to load email analytics:", err);
      }
    };
    const fetchCampaignsStats = async () => {
      if (!clientId) return;
      try {
        const res = await getCampaignAnalytics(clientId);
        // const normalizedRows = res.data.campaigns.map((res) => ({
        //   ...res,
        //   // startDate: new Date(res.startDate?.$date || res.startDate).toLocaleDateString(),
        //   // startTime: new Date(res.startTime?.$date || res.startTime).toLocaleTimeString(),
        // }));
        const campSummary = res.data.summary;
        //setCampaigns(normalizedRows);
        setCampaignsSummary(campSummary || { totalCampaigns: 0, activeCampaigns: 0, totalEmailsSent: 0, averageOpenRate: 0, averageClickRate: 0, averageBounceRate: 0});
      } catch (error) {
        setSnackbar({ open: true, message: 'Not able to fetch campaign details', severity: 'error' });
      }
    }; 
    fetchStats();
    fetchCampaignsStats();
  }, [clientId]);
 
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const res = await getDashboard(clientId);

        if (res.success) {
          const d = res.data;
          // Map summary
          setSummary({
            totalLeads: d.totalLeads,
            mailPerformance: d.mailPerformance,
            aiScore: d.averageAiScore,
            totalEmailLogs: d.totalEmailLogs,
          });

          // Map lead pipeline (convert object → array for your UI loop)
          const pipelineArr = [
            //{ label: "Generated", count: d.leadPipeline?.generated || 0 },
            { label: "New", count: d.leadPipeline?.new || 0 },
            { label: "InitialContact", count: d.leadPipeline?.initialContact || 0 },
            { label: "Followup", count: d.leadPipeline?.followup || 0 },
            // { label: "meetingScheduled", count: d.leadPipeline?.meetingScheduled || 0 }, 
            //{ label: "WonLost", count: d.leadPipeline?.wonLost || 0 },
          ];
          setPipeline(pipelineArr);

          // Map activities (UI expects type, desc, time)
          const activitiesArr = (d.recentActivity || []).map((a, i) => ({
            id: i + 1,
            type: a.type,
            desc: a.message,
            status: a.status,
            email: a.email,
            time: new Date(a.timestamp).toLocaleString(),
          }));
          setActivities(activitiesArr);
          // No alerts in backend yet → keep empty
          // setAlerts([]);
        } else {
          setSnackbar({ open: true, message: res.message, severity: "error" });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Unable to load data",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [clientId]);

  if (loading) {
    return (
      <Box sx={{display:'flex', justifyContent:'center', alignItems:'center', height:'50vh'}}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      // background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #1e40af 100%)',
      // backgroundImage: theme.palette.gradient.light,
      p: {xs:0, sm:3},
      borderRadius:3
    }}>
      {/* Error display via Alert and snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Grid container spacing={3}>
        {/* Top Row - Metric Cards */}
        <Grid size={{xs:6, sm:6, md:3}}>
          <Card sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            height: 140,
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                  Total Leads
                </Typography>
                <Box sx={{ 
                  // bgcolor: 'rgba(239, 68, 68, 0.2)', 
                  borderRadius: '50%', 
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                   {/* <TrendingUp sx={{ fontSize: 16, color: '#22c55e' }} /> */}
                   <LeaderboardRounded sx={{ fontSize: 18, color: "white" }}/>
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                {summary?.totalLeads || 0}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Total leads generated
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs:6, sm:6, md:3}}>
          <Card sx={{
            background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            height: 140,
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                  Emails Sent
                </Typography>
                <Box sx={{ 
                  // bgcolor: 'rgba(34, 197, 94, 0.2)', 
                  borderRadius: '50%', 
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {/* <TrendingDown sx={{ fontSize: 16, color: '#ef4444' }} /> */}
                  <EmailRounded sx={{ fontSize: 18, color: "white" }}/>
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                {stats?.sent || 0}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Total emails sent
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs:6, sm:6, md:3}}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f87171 0%, #E84393 100%)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            height: 140,
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                  AI Score
                </Typography>
                <Box sx={{ 
                  // bgcolor: 'rgba(239, 68, 68, 0.2)', 
                  borderRadius: '50%', 
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <SmartToy sx={{ fontSize: 18, color: "white" }} />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                {summary?.aiScore || 0}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Average AI score
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <Card sx={{
            background: 'linear-gradient(135deg, #7f70efff 0%, #5b4adbff 100%)',
            borderRadius: 3,
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: 140,
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                  Total Activities
                </Typography>
                <Box sx={{ 
                  // bgcolor: 'rgba(239, 68, 68, 0.2)', 
                  borderRadius: '50%', 
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <StackedBarChartRounded sx={{ fontSize: 18, color: "white" }} />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                {summary?.totalEmailLogs || 0}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Email activities
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Second Row - Charts */}
        {/* <Grid size={{xs:12, sm:6, md:6}}>
          <Card sx={{
            // background: 'rgba(30, 58, 138, 0.8)',
            background: theme.palette.gradient.primary,
                borderRadius: 3,
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: 320,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Key campaign analytics
              </Typography>
              <BarChart
                xAxis={[{ 
                  data: xLabels.length > 0 ? xLabels : ['No Data'], 
                  scaleType: 'band',
                  tickLabelStyle: { fill: 'white', fontSize: 12 }
                }]}
                yAxis={[{
                  tickLabelStyle: { fill: 'white', fontSize: 12 }
                }]}
                series={[
                  { data: openData.length > 0 ? openData : [0], color: '#3b82f6', name: 'Open Rate' },
                  { data: clickData.length > 0 ? clickData : [0], color: '#10b981', name: 'Click Rate' },
                  { data: bounceData.length > 0 ? bounceData : [0], color: '#f59e0b', name: 'Bounce Rate' }
                ]}
                height={220}
                slotProps={{
                  legend: {
                    labelStyle: { fill: 'white', fontSize: 12 }
                  }
                }}
              />
                </CardContent>
              </Card>
            </Grid> */}
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <Card sx={{
            // background: 'rgba(30, 58, 138, 0.8)',
            background: theme.palette.gradient.primaryLight,
            borderRadius: 3,
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Lead Pipeline Volume
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {summary?.totalLeads || 0}<Typography component="span" variant="body2" sx={{ opacity: 0.7 }}> Leads</Typography>
                </Typography>
              </Box>

              <BarChart
                xAxis={[{
                  data: pipeline.length > 0 ? pipeline.map(p => p.label) : ['No Data'],
                  scaleType: 'band',
                  tickLabelStyle: { fill: 'white', fontSize: 12 }
                }]}
                yAxis={[{
                  tickLabelStyle: { fill: 'white', fontSize: 12 }
                }]}
                series={[
                  { data: pipeline.length > 0 ? pipeline.map(p => p.count) : [0], color: '#3b82f6' }
                ]}
                height={200}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs:12, sm:6, md:6}}>
          <Card sx={{
            // background: 'rgba(30, 58, 138, 0.8)',
            background: theme.palette.gradient.primaryLight,
            borderRadius: 3,
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: 320,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Real-time analytics
                </Typography>
                {/* <IconButton size="small" sx={{ color: 'white' }}>
                  <MoreVert />
                </IconButton> */}
              </Box>
                <PieChart
                  series={[{
                    innerRadius: 60,
                    outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    data: [
                    { id: 0, value: stats.delivered || 0, label: 'Delivered', color: '#22c55e' },
                    { id: 1, value: stats.opened || 0, label: 'Opened', color: '#3b82f6' },
                    { id: 2, value: stats.clicked || 0, label: 'Clicked', color: '#f59e0b' },
                    { id: 3, value: stats.bounced || 0, label: 'Bounced', color: '#f87171' },
                  ]
                }]}
                height={220}
                slotProps={{
                  legend: {
                    labelStyle: { fill: 'white', fontSize: 12 },
                    direction: 'column',
                    position: { vertical: 'middle', horizontal: 'right' }
                  }
                }}
                />
              </CardContent>
            </Card>
          </Grid>

        {/* Third Row - Campaign Code and Conversation */}
        <Grid size={{xs:12, sm:6, md:6}}>
          <Card sx={{
            // background: 'rgba(30, 58, 138, 0.8)',
            background: theme.palette.gradient.primaryLight,
            borderRadius: 3,
                            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: 340,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Campaign performance metrics
              </Typography>
              {/* <Typography variant="body2" sx={{ opacity: 0.7, mb: 3 }}>
                Campaign performance metrics
              </Typography> */}
              
              <Box sx={{ mb: 3, pt:3}}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box sx={{ 
                    bgcolor: '#ef4444', 
                    borderRadius: '50%', 
                    width: 8, 
                    height: 8 
                  }} />
                  <Typography variant="body2">Total Campaigns</Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {campaignsSummary?.totalCampaigns || 0}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min((campaignsSummary?.totalCampaigns || 0) * 10, 100)} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': { bgcolor: '#ef4444', borderRadius: 3 }
                  }} 
                />
                </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box sx={{ 
                    bgcolor: '#3b82f6', 
                    borderRadius: '50%', 
                    width: 8, 
                    height: 8 
                  }} />
                  <Typography variant="body2">Active Campaigns</Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {campaignsSummary?.activeCampaigns || 0}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min((campaignsSummary?.activeCampaigns || 0) * 15, 100)} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6', borderRadius: 3 }
                  }} 
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box sx={{ 
                    bgcolor: '#f59e0b', 
                    borderRadius: '50%', 
                    width: 8, 
                    height: 8 
                  }} />
                  <Typography variant="body2">Completed Campaigns</Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {campaignsSummary?.completedCampaigns || 0}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min((campaignsSummary?.completedCampaigns || 0) * 15, 100)} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': { bgcolor: '#f59e0b', borderRadius: 3 }
                  }} 
                />
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box sx={{ 
                    bgcolor: '#10b981', 
                    borderRadius: '50%', 
                    width: 8, 
                    height: 8 
                  }} />
                  <Typography variant="body2">Avg Open Rate</Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {(campaignsSummary?.averageOpenRate ?? 0).toFixed(1) || 0}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(campaignsSummary?.averageOpenRate ?? 0).toFixed(1) || 0} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': { bgcolor: '#10b981', borderRadius: 3 }
                  }} 
                />
              </Box>
              </CardContent>
            </Card>
          </Grid>

        <Grid size={{xs:12, sm:6, md:6}}>
          <Card sx={{
            // background: 'rgba(30, 58, 138, 0.8)',
            background: theme.palette.gradient.primaryLight,
            borderRadius: 3,
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: 340,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Recent Activities
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Latest {activities.length}
                </Typography>
              </Box>
              
              {activities.length > 0 ? activities.slice(0, 5).map((activity, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Avatar sx={{ 
                    bgcolor: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'][index % 4], 
                    width: 32, 
                    height: 32, 
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}>
                    {activity.email ? activity.email.charAt(0).toUpperCase() : 'A'}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {activity.email || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {activity.status}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {activity.type}
                  </Typography>
                </Box>
              )) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    No recent activities
                  </Typography>
                </Box>
              )}
              </CardContent>
            </Card>
          </Grid>

        </Grid>
    </Box>
  );
}


